import { useEffect, useState } from 'react';
import { useProjectStore } from '../../store/useProjectStore';
import { useExtractionStore } from '../../store/useExtractionStore';
import { useCollectionStore } from '../../store/useCollectionStore';
import * as api from '../../api/endpoints';
import { AddComponentModal } from './AddComponentModal';
import type {
  ExtractionPageMeta,
  ExtractedComponent,
  ExtractionPageResult,
  ApprovalRequest,
} from '../../types';

// ── Shared helpers for per-file approval ─────────────────────

/** Collect all pending reviews for a file (including currentReview). */
function collectFileReviews(file: string): ApprovalRequest[] {
  const ext = useExtractionStore.getState();
  const reviews = ext.pendingReviews.filter(r => r.file === file);
  // currentReview is stored separately — include it if it matches
  if (ext.currentReview?.file === file) {
    reviews.unshift(ext.currentReview);
  }
  return reviews;
}

/** Approve all pending reviews for a file (parallel API calls). */
export async function approveFileReviews(file: string, instructions?: string) {
  const ext = useExtractionStore.getState();
  const fileReviews = collectFileReviews(file);
  if (fileReviews.length === 0) return;

  // Fire all approval API calls in parallel
  await Promise.all(fileReviews.map(review => {
    const key = `${review.file}:${review.page}`;
    const savedBboxes = ext.pageBboxes[key] || [];
    const contentBboxes = savedBboxes
      .filter(b => b.type === 'content')
      .map(b => b.bbox as number[]);
    const contextBlocks = savedBboxes
      .filter(b => b.type !== 'content')
      .map(b => ({ type: b.type, bbox: b.bbox, label: b.label }));

    ext.setPageStatus(key, 'extracting');
    return api.approvePageReview(review.request_id, {
      content_bboxes: contentBboxes.length > 0 ? contentBboxes : undefined,
      content_bbox: contentBboxes.length === 1 ? contentBboxes[0] : undefined,
      context_blocks: contextBlocks.length > 0 ? contextBlocks : undefined,
      instructions,
    });
  }));

  // Atomically remove this file's reviews from the store
  ext.removeFileReviews(file);
}

/** Skip all pending reviews for a file. */
export async function skipFileReviews(file: string) {
  const fileReviews = collectFileReviews(file);
  if (fileReviews.length === 0) return;

  const ext = useExtractionStore.getState();
  await Promise.all(fileReviews.map(review => {
    const key = `${review.file}:${review.page}`;
    ext.setPageStatus(key, 'skipped');
    return api.skipPageReview(review.request_id);
  }));

  ext.removeFileReviews(file);
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Waiting',
  reviewing: 'Review Required',
  extracting: 'Extracting...',
  done: 'Complete',
  skipped: 'Skipped',
};

/** Returns a display-friendly status, considering whether data already exists. */
function getDisplayStatus(status: string, pageKey: string): { label: string; cssStatus: string } {
  if (status === 'reviewing') {
    const { pageResults } = useExtractionStore.getState();
    const hasData = pageResults[pageKey]?.new_components?.length;
    if (hasData) {
      return { label: 'Has Data — Re-extract?', cssStatus: 'reviewing-has-data' };
    }
  }
  return { label: STATUS_LABELS[status] || status, cssStatus: status };
}

export function ExtractionRight() {
  const pendingReviews = useExtractionStore((s) => s.pendingReviews);
  const pages = useExtractionStore((s) => s.pages);
  const pageIndex = useExtractionStore((s) => s.pageIndex);
  const pageStatuses = useExtractionStore((s) => s.pageStatuses);
  const currentPage = pages[pageIndex];

  const pageKey = currentPage ? `${currentPage.file}:${currentPage.page}` : '';
  const pageStatus = currentPage ? (pageStatuses[pageKey] || 'pending') : 'pending';

  // Count pending reviews for the current file
  const currentFile = currentPage?.file || '';
  const filePendingCount = pendingReviews.filter(r => r.file === currentFile).length;
  const hasFileReviews = filePendingCount > 0;

  const displayStatus = currentPage ? getDisplayStatus(pageStatus, pageKey) : null;

  return (
    <div className="extraction-right">
      {/* Page header with prominent status */}
      {currentPage && displayStatus && (
        <div className={`extraction-page-header status-${displayStatus.cssStatus}`}>
          <div className="page-header-top">
            <span className="page-header-label">
              p.{currentPage.page}
            </span>
            <span className={`page-status-pill ${displayStatus.cssStatus}`}>
              {displayStatus.label}
            </span>
          </div>
          <div className="page-header-meta">
            <span>{currentPage.file}</span>
            <span className="page-header-sep" />
            <span>{currentPage.docType.replace(/_/g, ' ')}</span>
            <span className="page-header-sep" />
            <span>{currentPage.zoneId}</span>
          </div>
          {currentPage.title && <div className="page-header-title">{currentPage.title}</div>}
        </div>
      )}

      {/* Document process panel — show when current file has pending reviews */}
      {hasFileReviews && <DocumentProcessPanel file={currentFile} count={filePendingCount} />}

      {/* Status-specific messages */}
      {pageStatus === 'pending' && !hasFileReviews && (
        <div className="page-state-message">
          <span className="state-dot pending" />
          Waiting for extraction to reach this page
        </div>
      )}
      {pageStatus === 'extracting' && (
        <div className="page-state-message extracting">
          <span className="state-dot extracting" />
          LLM is analyzing this page...
        </div>
      )}

      <div className="data-header">
        <h3>Extracted Data</h3>
      </div>
      <ExtractedDataPanel />
    </div>
  );
}

// ── Document Process Panel ──────────────────────────────────

function DocumentProcessPanel({ file, count }: { file: string; count: number }) {
  const pendingReviews = useExtractionStore((s) => s.pendingReviews);
  const pageResults = useExtractionStore((s) => s.pageResults);
  const extraction = useProjectStore((s) => s.extraction);
  const [instructions, setInstructions] = useState('');
  const [processing, setProcessing] = useState(false);

  // Extract context from first pending review for this file
  const firstReview = pendingReviews.find(r => r.file === file);
  const ctx = (firstReview?.context || {}) as Record<string, unknown>;

  // Check if this file already has extracted data (from previous run)
  const existingComponentCount = (() => {
    let total = 0;
    // Check live page results
    for (const [key, result] of Object.entries(pageResults)) {
      if (key.startsWith(file + ':') && result?.new_components) {
        total += result.new_components.length;
      }
    }
    // Also check full extraction data
    if (total === 0 && extraction?.zones) {
      for (const zone of extraction.zones) {
        for (const page of zone.pages || []) {
          if (page.file === file && page.new_components) {
            total += page.new_components.length;
          }
        }
      }
    }
    return total;
  })();
  const hasExistingData = existingComponentCount > 0;

  useEffect(() => {
    if (ctx.instructions) {
      setInstructions(ctx.instructions as string);
    }
  }, [file]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProcessDocument = async () => {
    setProcessing(true);
    try {
      await approveFileReviews(file, instructions || undefined);
    } catch (e) {
      useProjectStore.getState().setToast(`Process failed: ${e}`, 'error');
    }
    setProcessing(false);
  };

  const handleSkipDocument = async () => {
    setProcessing(true);
    try {
      await skipFileReviews(file);
    } catch (e) {
      useProjectStore.getState().setToast(`Skip failed: ${e}`, 'error');
    }
    setProcessing(false);
  };

  return (
    <div className="extraction-review-panel">
      <div className="extraction-review-content">
        <div className="extraction-review-header">
          <span className="page-review-badge">
            {hasExistingData ? 'RE-EXTRACTION' : 'DOCUMENT REVIEW'}
          </span>
          <span className="approval-skill">
            {file} — {count} page{count !== 1 ? 's' : ''}
          </span>
        </div>

        {hasExistingData && (
          <div className="review-existing-notice">
            Previous results: {existingComponentCount} components already extracted.
            You can keep them or re-extract.
          </div>
        )}

        {Boolean(ctx['project_context']) && (
          <div className="approval-context">
            {Object.entries(ctx).map(([k, v]) => {
              if (k === 'instructions' || !v) return null;
              return (
                <span key={k} className="context-chip">
                  {k}: {String(v as string)}
                </span>
              );
            })}
          </div>
        )}

        <div className="approval-instructions-section">
          <label className="approval-label">Instructions for extraction</label>
          <textarea
            className="approval-instructions"
            rows={2}
            placeholder="Optional: guidance for all pages in this document..."
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
          />
        </div>

        <div className="doc-review-hint">
          {hasExistingData
            ? 'Re-extracting will replace existing results. Skip to keep current data.'
            : 'Browse pages to adjust content/context regions, then process the entire document.'}
        </div>

        <div className="approval-actions">
          {hasExistingData ? (
            <>
              <button className="btn-approve" onClick={handleSkipDocument} disabled={processing}>
                {processing ? '...' : 'Keep Existing'}
              </button>
              <button className="btn-reject" onClick={handleProcessDocument} disabled={processing}>
                {processing ? '...' : `Re-extract (${count})`}
              </button>
            </>
          ) : (
            <>
              <button className="btn-approve" onClick={handleProcessDocument} disabled={processing}>
                {processing ? 'Processing...' : `Process Document (${count} pages)`}
              </button>
              <button className="btn-reject" onClick={handleSkipDocument} disabled={processing}>
                Skip Document
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Extracted Data Panel ────────────────────────────────────

function ExtractedDataPanel() {
  const pages = useExtractionStore((s) => s.pages);
  const pageIndex = useExtractionStore((s) => s.pageIndex);
  const pageStatuses = useExtractionStore((s) => s.pageStatuses);
  const pageResults = useExtractionStore((s) => s.pageResults);
  const extraction = useProjectStore((s) => s.extraction);
  const renders = useProjectStore((s) => s.renders);

  const currentPage = pages[pageIndex];
  if (!currentPage) {
    return <div className="data-panel"><div className="feed-placeholder">Select a page</div></div>;
  }

  const key = `${currentPage.file}:${currentPage.page}`;
  const pageStatus = pageStatuses[key] || 'pending';

  // Try live SSE result first, then full skill data
  let pageData: ExtractionPageResult | null = pageResults[key] || null;
  if (!pageData && extraction) {
    for (const zone of extraction.zones || []) {
      for (const pg of zone.pages || []) {
        if (pg.file === currentPage.file && pg.page === currentPage.page) {
          pageData = pg;
          break;
        }
      }
      if (pageData) break;
    }
  }

  // HQ render preview
  const panelRenders = renders['panel'] || [];
  const renderFile = panelRenders.find(
    (r) => r.includes(currentPage.file.replace('.pdf', '')) && r.includes(`_p${currentPage.page}`),
  );

  return (
    <div className="data-panel">
      {renderFile && (
        <div className="hq-render-preview">
          <img src={api.renderUrl('panel', renderFile)} alt="HQ render" />
        </div>
      )}

      {pageData ? (
        <PageExtractionData
          data={pageData}
          page={currentPage}
          pageStatus={pageStatus}
        />
      ) : (
        <NoDataPanel page={currentPage} pageStatus={pageStatus} />
      )}
    </div>
  );
}

function NoDataPanel({ page, pageStatus }: { page: ExtractionPageMeta; pageStatus: string }) {
  const [extracting, setExtracting] = useState(false);

  const handleExtractDocument = async () => {
    setExtracting(true);
    useProjectStore.getState().setToast(`Extracting ${page.file}...`, 'info');
    try {
      const result = await api.extractDocument(page.file, page.zoneId || 'default');
      if (result) {
        useProjectStore.getState().setToast(
          `Extracted ${result.pages_extracted} pages, ${result.total_components} components`,
          'success',
        );
        await refreshExtractionStores(page);
      }
    } catch (e) {
      useProjectStore.getState().setToast(`Extract failed: ${e}`, 'error');
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="feed-placeholder">
      <div>No extraction data for this page yet</div>
      {pageStatus === 'pending' && (
        <button
          className="btn-reextract"
          style={{ marginTop: 12 }}
          onClick={handleExtractDocument}
          disabled={extracting}
        >
          {extracting ? 'Extracting document...' : `Extract document (${page.file.replace(/\.pdf$/i, '')})`}
        </button>
      )}
    </div>
  );
}


function PageExtractionData({
  data,
  page,
  pageStatus,
}: {
  data: ExtractionPageResult;
  page: ExtractionPageMeta;
  pageStatus: string;
}) {
  const [reExtracting, setReExtracting] = useState(false);
  const [addModal, setAddModal] = useState<{ mode: 'add' | 'duplicate'; source?: ExtractedComponent } | null>(null);
  const components = data.new_components || [];
  const cables = data.cables_and_labor || [];
  const auxCount = data.auxiliary_contacts_count || 0;
  const ignored = data.ignored_items || [];
  const reasoning = data.reasoning_summary;

  const handleReExtract = async () => {
    if (!confirm('Re-extract this page? Current results will be replaced.')) return;
    setReExtracting(true);
    try {
      const bboxes = useExtractionStore.getState().currentBboxes;
      const contentBboxes = bboxes.filter(b => b.type === 'content');
      const contextBlocks = bboxes
        .filter(b => b.type !== 'content')
        .map(b => ({ type: b.type, bbox: b.bbox, label: b.label }));

      const result = await api.reExtractPage(page.zoneId, page.file, page.page, {
        content_bbox: contentBboxes.length === 1 ? contentBboxes[0].bbox : undefined,
        content_bboxes: contentBboxes.length > 1 ? contentBboxes.map(b => b.bbox) : undefined,
        context_blocks: contextBlocks.length > 0 ? contextBlocks : undefined,
      });
      if (result) {
        await refreshExtractionStores(page);
        useCollectionStore.getState().touchDiagram(page.file);
      }
    } catch (e) {
      useProjectStore.getState().setToast(`Re-extract failed: ${e}`, 'error');
    } finally {
      setReExtracting(false);
    }
  };

  return (
    <div className="extraction-data-content">
      {addModal && (
        <AddComponentModal
          mode={addModal.mode}
          page={page}
          sourceComponent={addModal.source}
          onClose={() => setAddModal(null)}
          onSaved={() => {}}
        />
      )}

      {reasoning && (
        <details className="reasoning-block">
          <summary className="reasoning-toggle">Reasoning</summary>
          <div className="event-body">{reasoning}</div>
        </details>
      )}

      <div className="section-header">
        Components ({components.length})
        <div className="section-actions">
          <button
            className="btn-add-component"
            onClick={() => setAddModal({ mode: 'add' })}
          >
            + Add
          </button>
          {(pageStatus === 'done' || pageStatus === 'reviewing') && (
            <button
              className="btn-reextract"
              onClick={handleReExtract}
              disabled={reExtracting}
            >
              {reExtracting ? 'Re-extracting...' : 'Re-extract'}
            </button>
          )}
        </div>
      </div>

      {components.length > 0 ? (
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Category</th>
              <th>Spec</th>
              <th>Qty</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {components.map((c, i) => (
              <ComponentRow
                key={c.id || i}
                component={c}
                page={page}
                onDuplicate={(src) => setAddModal({ mode: 'duplicate', source: src })}
              />
            ))}
          </tbody>
        </table>
      ) : (
        <div className="text-dim" style={{ padding: '4px 8px', fontSize: 12 }}>
          No components
        </div>
      )}

      {cables.length > 0 && (
        <>
          <div className="section-header">Cables & Labor ({cables.length})</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Type</th>
                <th>Length (m)</th>
                <th>Terminals</th>
                <th>Terminations</th>
              </tr>
            </thead>
            <tbody>
              {cables.map((c, i) => (
                <tr key={i}>
                  <td className="mono">{c.cable_id}</td>
                  <td>{c.cable_type || ''}</td>
                  <td>{c.length_m != null ? c.length_m : '—'}</td>
                  <td>{c.terminal_pin_count || 0}</td>
                  <td>{c.labor_terminations || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {auxCount > 0 && (
        <div className="section-header">Auxiliary Contacts: {auxCount}</div>
      )}

      {ignored.length > 0 && (
        <>
          <div className="section-header">Ignored ({ignored.length})</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {ignored.map((item, i) => (
                <tr key={i}>
                  <td>{item.description}</td>
                  <td className="text-dim">{item.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function ComponentRow({
  component,
  page,
  onDuplicate,
}: {
  component: ExtractedComponent;
  page: ExtractionPageMeta;
  onDuplicate: (source: ExtractedComponent) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(component.id);
  const [editCategory, setEditCategory] = useState(component.category);
  const [editSpec, setEditSpec] = useState(component.spec_grouping_string || '');

  const handleSave = async () => {
    setBusy(true);
    try {
      await api.editComponent(page.zoneId, page.file, page.page, component.id, {
        id: editId,
        category: editCategory,
        spec_grouping_string: editSpec,
      });
      setEditing(false);
      await refreshExtractionStores(page);
      useCollectionStore.getState().touchDiagram(page.file);
    } catch (e) {
      useProjectStore.getState().setToast(`Edit failed: ${e}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete component ${component.id}?`)) return;
    setBusy(true);
    try {
      await api.deleteComponent(page.zoneId, page.file, page.page, component.id);
      await refreshExtractionStores(page);
      useCollectionStore.getState().touchDiagram(page.file);
    } catch (e) {
      useProjectStore.getState().setToast(`Delete failed: ${e}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <tr className="editing-row">
        <td><input value={editId} onChange={(e) => setEditId(e.target.value)} className="inline-input" /></td>
        <td><input value={editCategory} onChange={(e) => setEditCategory(e.target.value)} className="inline-input" /></td>
        <td><input value={editSpec} onChange={(e) => setEditSpec(e.target.value)} className="inline-input" /></td>
        <td>{component.quantity || 1}</td>
        <td>
          <button className="btn-sm btn-save" onClick={handleSave} disabled={busy}>{busy ? '...' : 'Save'}</button>
          <button className="btn-sm btn-cancel" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
        </td>
      </tr>
    );
  }

  return (
    <tr className={busy ? 'row-busy' : ''}>
      <td className="mono">{component.id}</td>
      <td>{component.category}</td>
      <td className="mono">{component.spec_grouping_string || ''}</td>
      <td className="text-center">{component.quantity || 1}</td>
      <td className="action-cell">
        <button className="btn-sm" onClick={() => setEditing(true)} title="Edit" disabled={busy}>&#9998;</button>
        <button className="btn-sm" onClick={() => onDuplicate(component)} title="Duplicate" disabled={busy}>&#9111;</button>
        <button className="btn-sm btn-danger" onClick={handleDelete} title="Delete" disabled={busy}>&times;</button>
      </td>
    </tr>
  );
}

/** Refresh both project and extraction stores after a CRUD operation */
async function refreshExtractionStores(page: ExtractionPageMeta) {
  const ext = await api.getExtraction();
  if (ext) {
    useProjectStore.getState().setExtraction(ext);
    const key = `${page.file}:${page.page}`;
    for (const zone of ext.zones) {
      for (const pg of zone.pages) {
        if (pg.file === page.file && pg.page === page.page) {
          useExtractionStore.getState().setPageResult(key, pg);
          return;
        }
      }
    }
  }
}
